import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import OwnerPageLayout from '../../components/layout/OwnerPageLayout';
import ProfileCompletion from '../../components/profile/ProfileCompletion';
import SportTypeSelector from '../../components/SportTypeSelector';
import {
    getMySportProfile, updateSportProfile, updateProfileSlug, getQualificationSystems,
    addAchievement, deleteAchievement, addQualification, addCareerEntry, addEducation,
    uploadProfilePhoto, uploadProfileCover, submitVerification,
} from '../../services/marketplaceApi';
import '../../styles/marketplace.css';

const TABS = ['basic', 'achievements', 'qualifications', 'career', 'education'];

const ACH_TYPES = ['competition', 'championship', 'cup', 'tournament', 'award', 'record'];
const LEVELS = ['city', 'region', 'national', 'international', 'world', 'olympic'];
const RESULTS = ['1', '2', '3', 'finalist', 'semifinalist', 'participant', 'winner', 'record'];

function verificationBadge(status) {
    if (status === 'verified') return { cls: 'verified', text: '✅ Подтверждено' };
    if (status === 'rejected') return { cls: 'rejected', text: '❌ Отклонено' };
    if (status === 'pending') return { cls: 'pending', text: '⏳ На проверке' };
    return null;
}

export default function SportProfileEditPage() {
    const { t } = useTranslation(['marketplace', 'common', 'nav', 'profile']);
    const [tab, setTab] = useState('basic');
    const [profile, setProfile] = useState(null);
    const [systems, setSystems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    const [achForm, setAchForm] = useState({ title: '', achievementType: 'competition', level: 'city', result: '1', year: new Date().getFullYear() });
    const [qualForm, setQualForm] = useState({ systemId: '', qualificationLevelId: '', issuedDate: '', issuedBy: '' });
    const [careerForm, setCareerForm] = useState({ entityType: 'school', externalName: '', role: 'athlete', startDate: '' });
    const [eduForm, setEduForm] = useState({ educationType: 'course', institution: '', specialty: '' });
    const [slugDraft, setSlugDraft] = useState('');
    const [slugError, setSlugError] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const [p, s] = await Promise.all([getMySportProfile(), getQualificationSystems()]);
            setProfile(p);
            setSlugDraft(p?.slug || '');
            setSystems(s || []);
        } catch (e) {
            setError(e.response?.data?.message || t('common:error'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => { load(); }, [load]);

    const tips = useMemo(() => {
        if (!profile) return [];
        return [
            !profile.photoUrl && t('marketplace:profileEdit.tipPhoto'),
            !profile.bio && t('marketplace:profileEdit.tipBio'),
            !(profile.achievements?.length) && t('marketplace:profileEdit.tipAchievements'),
            !(profile.qualifications?.length) && t('marketplace:profileEdit.tipQualifications'),
        ].filter(Boolean);
    }, [profile, t]);

    const selectedSystem = systems.find((s) => String(s.id) === String(qualForm.systemId));

    async function saveBasic(e) {
        e.preventDefault();
        setSaving(true);
        try {
            const updated = await updateSportProfile({
                displayName: profile.displayName,
                tagline: profile.tagline,
                bio: profile.bio,
                birthYear: profile.birthYear ? Number(profile.birthYear) : null,
                gender: profile.gender,
                country: profile.country,
                region: profile.region,
                city: profile.city,
                primarySportTypeId: profile.primarySportTypeId,
                instagram: profile.instagram,
                vk: profile.vk,
                telegram: profile.telegram,
                website: profile.website,
            });
            setProfile(updated);
        } catch (e) {
            setError(e.response?.data?.message || t('common:error'));
        } finally {
            setSaving(false);
        }
    }

    async function onPhoto(file) {
        const res = await uploadProfilePhoto(file);
        setProfile((p) => ({ ...p, photoUrl: res.photoUrl }));
        load();
    }

    async function onCover(file) {
        const res = await uploadProfileCover(file);
        setProfile((p) => ({ ...p, coverPhotoUrl: res.coverPhotoUrl }));
    }

    async function submitAch(e) {
        e.preventDefault();
        await addAchievement({ ...achForm, year: Number(achForm.year) });
        setAchForm({ title: '', achievementType: 'competition', level: 'city', result: '1', year: new Date().getFullYear() });
        load();
    }

    async function saveSlug(e) {
        e.preventDefault();
        setSlugError('');
        try {
            const res = await updateProfileSlug(slugDraft);
            setProfile((p) => ({ ...p, slug: res.slug }));
            alert(t('profile:slug.saved'));
        } catch (err) {
            const data = err.response?.data;
            if (data?.error === 'slug_taken') {
                setSlugError(t('profile:slug.taken'));
                if (data.suggestion) setSlugDraft(data.suggestion);
            } else {
                setSlugError(t('common:error'));
            }
        }
    }

    async function verifyAchievement(id) {
        const fd = new FormData();
        fd.append('entityType', 'sport_achievement');
        fd.append('sportAchievementId', String(id));
        await submitVerification(fd);
        load();
    }

    if (loading) return <OwnerPageLayout title={t('marketplace:profileEdit.title')}><p>{t('common:loading')}</p></OwnerPageLayout>;
    if (!profile) return <OwnerPageLayout title={t('marketplace:profileEdit.title')}><p>{error || t('common:error')}</p></OwnerPageLayout>;

    return (
        <OwnerPageLayout
            title={t('marketplace:profileEdit.title')}
            subtitle={t('marketplace:profileEdit.subtitle')}
            maxWidth={900}
            className="mp-page"
        >
            <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                <Link to="/profile/marketplace-settings" className="sp-btn sp-btn--outline">{t('marketplace:visibility.title')}</Link>
                <Link to="/marketplace" className="sp-btn sp-btn--outline">{t('marketplace:title')}</Link>
            </div>

            <ProfileCompletion percent={profile.completionPercent} tips={tips} />

            <div className="mp-edit-tabs">
                {TABS.map((key) => (
                    <button key={key} type="button" className={tab === key ? 'active' : ''} onClick={() => setTab(key)}>
                        {t(`marketplace:profileEdit.tabs.${key}`)}
                    </button>
                ))}
            </div>

            {error && <p className="cashier-error">{error}</p>}

            {tab === 'basic' && (
                <form onSubmit={saveBasic} className="sp-panel">
                    <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
                        <label>
                            {t('marketplace:profileEdit.photo')}
                            <input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && onPhoto(e.target.files[0])} />
                            {profile.photoUrl && <img src={profile.photoUrl} alt="" style={{ width: 80, height: 80, borderRadius: '50%', marginTop: 8, objectFit: 'cover' }} />}
                        </label>
                        <label>
                            {t('marketplace:profileEdit.cover')}
                            <input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && onCover(e.target.files[0])} />
                        </label>
                    </div>
                    <div className="mp-form-grid">
                        <label>{t('marketplace:profileEdit.displayName')}<input value={profile.displayName || ''} onChange={(e) => setProfile({ ...profile, displayName: e.target.value })} /></label>
                        <label>{t('marketplace:profileEdit.tagline')}<input value={profile.tagline || ''} onChange={(e) => setProfile({ ...profile, tagline: e.target.value })} /></label>
                        <label style={{ gridColumn: '1 / -1' }}>{t('marketplace:profileEdit.bio')}<textarea rows={4} value={profile.bio || ''} onChange={(e) => setProfile({ ...profile, bio: e.target.value })} /></label>
                        <label>{t('marketplace:profileEdit.birthYear')}<input type="number" value={profile.birthYear || ''} onChange={(e) => setProfile({ ...profile, birthYear: e.target.value })} /></label>
                        <label>{t('marketplace:profileEdit.gender')}
                            <select value={profile.gender || ''} onChange={(e) => setProfile({ ...profile, gender: e.target.value })}>
                                <option value="">—</option>
                                <option value="male">{t('marketplace:profileEdit.male')}</option>
                                <option value="female">{t('marketplace:profileEdit.female')}</option>
                            </select>
                        </label>
                        <label>{t('marketplace:profileEdit.country')}<input value={profile.country || ''} onChange={(e) => setProfile({ ...profile, country: e.target.value })} /></label>
                        <label>{t('marketplace:profileEdit.region')}<input value={profile.region || ''} onChange={(e) => setProfile({ ...profile, region: e.target.value })} /></label>
                        <label>{t('marketplace:profileEdit.city')}<input value={profile.city || ''} onChange={(e) => setProfile({ ...profile, city: e.target.value })} /></label>
                    </div>
                    <div style={{ marginTop: 12 }}>
                        <SportTypeSelector value={profile.primarySportTypeId} onChange={(id) => setProfile({ ...profile, primarySportTypeId: id })} />
                    </div>
                    <div className="sp-panel" style={{ marginTop: 16 }}>
                        <h4>{t('profile:slug.label')}</h4>
                        <p style={{ color: 'var(--sp-gray-600)', fontSize: 14 }}>{t('profile:slug.hint', { slug: profile.slug || '…' })}</p>
                        <p style={{ fontSize: 13, color: 'var(--sp-gray-500)' }}>{t('profile:slug.rules')}</p>
                        <form onSubmit={saveSlug} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                            <input value={slugDraft} onChange={(e) => setSlugDraft(e.target.value)} style={{ flex: 1, minWidth: 200 }} />
                            <button type="submit" className="sp-btn sp-btn--outline">{t('profile:slug.edit')}</button>
                        </form>
                        {slugError && <p className="cashier-error">{slugError}</p>}
                    </div>
                    <div className="mp-form-grid" style={{ marginTop: 12 }}>
                        <label>Instagram<input value={profile.instagram || ''} onChange={(e) => setProfile({ ...profile, instagram: e.target.value })} /></label>
                        <label>VK<input value={profile.vk || ''} onChange={(e) => setProfile({ ...profile, vk: e.target.value })} /></label>
                        <label>Telegram<input value={profile.telegram || ''} onChange={(e) => setProfile({ ...profile, telegram: e.target.value })} /></label>
                        <label>Website<input value={profile.website || ''} onChange={(e) => setProfile({ ...profile, website: e.target.value })} /></label>
                    </div>
                    <button type="submit" className="sp-btn sp-btn--primary" style={{ marginTop: 16 }} disabled={saving}>{saving ? t('common:saving') : t('common:save')}</button>
                </form>
            )}

            {tab === 'achievements' && (
                <div>
                    {(profile.achievements || []).map((a) => {
                        const b = verificationBadge(a.verificationStatus);
                        return (
                            <div key={a.id} className="mp-list-card">
                                <strong>{a.title}</strong> — {a.result} ({a.year})
                                {b && <span className={`mp-badge ${b.cls}`}>{b.text}</span>}
                                <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                                    {a.verificationStatus !== 'verified' && (
                                        <button type="button" className="sp-btn sp-btn--outline" onClick={() => verifyAchievement(a.id)}>{t('marketplace:verification.submit')}</button>
                                    )}
                                    <button type="button" className="sp-btn sp-btn--outline" onClick={() => deleteAchievement(a.id).then(load)}>{t('common:delete')}</button>
                                </div>
                            </div>
                        );
                    })}
                    <form onSubmit={submitAch} className="sp-panel" style={{ marginTop: 12 }}>
                        <h3>{t('marketplace:profileEdit.addAchievement')}</h3>
                        <div className="mp-form-grid">
                            <label>{t('marketplace:profileEdit.achTitle')}<input required value={achForm.title} onChange={(e) => setAchForm({ ...achForm, title: e.target.value })} /></label>
                            <label>{t('marketplace:profileEdit.achType')}
                                <select value={achForm.achievementType} onChange={(e) => setAchForm({ ...achForm, achievementType: e.target.value })}>
                                    {ACH_TYPES.map((x) => <option key={x} value={x}>{x}</option>)}
                                </select>
                            </label>
                            <label>{t('marketplace:profileEdit.achLevel')}
                                <select value={achForm.level} onChange={(e) => setAchForm({ ...achForm, level: e.target.value })}>
                                    {LEVELS.map((x) => <option key={x} value={x}>{x}</option>)}
                                </select>
                            </label>
                            <label>{t('marketplace:profileEdit.achResult')}
                                <select value={achForm.result} onChange={(e) => setAchForm({ ...achForm, result: e.target.value })}>
                                    {RESULTS.map((x) => <option key={x} value={x}>{x}</option>)}
                                </select>
                            </label>
                            <label>{t('marketplace:profileEdit.achYear')}<input type="number" value={achForm.year} onChange={(e) => setAchForm({ ...achForm, year: e.target.value })} /></label>
                        </div>
                        <button type="submit" className="sp-btn sp-btn--primary" style={{ marginTop: 12 }}>{t('common:add')}</button>
                    </form>
                </div>
            )}

            {tab === 'qualifications' && (
                <div>
                    {(profile.qualifications || []).map((q) => (
                        <div key={q.id} className="mp-list-card">
                            {q.level?.name || q.level?.nameRu || q.customTitle}
                            {q.issuedBy && <span> — {q.issuedBy}</span>}
                        </div>
                    ))}
                    <form className="sp-panel" onSubmit={async (e) => {
                        e.preventDefault();
                        await addQualification({
                            qualificationLevelId: qualForm.qualificationLevelId ? Number(qualForm.qualificationLevelId) : null,
                            issuedBy: qualForm.issuedBy,
                            issuedDate: qualForm.issuedDate || null,
                        });
                        load();
                    }}>
                        <h3>{t('marketplace:profileEdit.addQualification')}</h3>
                        <div className="mp-form-grid">
                            <label>{t('marketplace:profileEdit.qualSystem')}
                                <select value={qualForm.systemId} onChange={(e) => setQualForm({ ...qualForm, systemId: e.target.value, qualificationLevelId: '' })}>
                                    <option value="">—</option>
                                    {systems.map((s) => <option key={s.id} value={s.id}>{s.systemName}</option>)}
                                </select>
                            </label>
                            <label>{t('marketplace:profileEdit.qualLevel')}
                                <select value={qualForm.qualificationLevelId} onChange={(e) => setQualForm({ ...qualForm, qualificationLevelId: e.target.value })} disabled={!selectedSystem}>
                                    <option value="">—</option>
                                    {(selectedSystem?.levels || []).map((l) => <option key={l.id} value={l.id}>{l.name || l.nameRu}</option>)}
                                </select>
                            </label>
                            <label>{t('marketplace:profileEdit.qualDate')}<input type="date" value={qualForm.issuedDate} onChange={(e) => setQualForm({ ...qualForm, issuedDate: e.target.value })} /></label>
                            <label>{t('marketplace:profileEdit.qualIssuer')}<input value={qualForm.issuedBy} onChange={(e) => setQualForm({ ...qualForm, issuedBy: e.target.value })} /></label>
                        </div>
                        <button type="submit" className="sp-btn sp-btn--primary" style={{ marginTop: 12 }}>{t('common:add')}</button>
                    </form>
                </div>
            )}

            {tab === 'career' && (
                <div>
                    {(profile.careerHistory || []).map((c) => (
                        <div key={c.id} className="mp-list-card">{c.externalName || c.role} — {c.startDate?.slice(0, 10)}</div>
                    ))}
                    <form className="sp-panel" onSubmit={async (e) => {
                        e.preventDefault();
                        await addCareerEntry({ ...careerForm, startDate: careerForm.startDate });
                        load();
                    }}>
                        <h3>{t('marketplace:profileEdit.addCareer')}</h3>
                        <div className="mp-form-grid">
                            <label>{t('marketplace:profileEdit.careerName')}<input value={careerForm.externalName} onChange={(e) => setCareerForm({ ...careerForm, externalName: e.target.value })} /></label>
                            <label>{t('marketplace:profileEdit.careerRole')}<input value={careerForm.role} onChange={(e) => setCareerForm({ ...careerForm, role: e.target.value })} /></label>
                            <label>{t('marketplace:profileEdit.careerStart')}<input type="date" required value={careerForm.startDate} onChange={(e) => setCareerForm({ ...careerForm, startDate: e.target.value })} /></label>
                        </div>
                        <button type="submit" className="sp-btn sp-btn--primary" style={{ marginTop: 12 }}>{t('common:add')}</button>
                    </form>
                </div>
            )}

            {tab === 'education' && (
                <div>
                    {(profile.education || []).map((ed) => (
                        <div key={ed.id} className="mp-list-card">{ed.institution} — {ed.specialty}</div>
                    ))}
                    <form className="sp-panel" onSubmit={async (e) => {
                        e.preventDefault();
                        await addEducation(eduForm);
                        load();
                    }}>
                        <h3>{t('marketplace:profileEdit.addEducation')}</h3>
                        <div className="mp-form-grid">
                            <label>{t('marketplace:profileEdit.eduType')}<input value={eduForm.educationType} onChange={(e) => setEduForm({ ...eduForm, educationType: e.target.value })} /></label>
                            <label>{t('marketplace:profileEdit.eduInstitution')}<input value={eduForm.institution} onChange={(e) => setEduForm({ ...eduForm, institution: e.target.value })} /></label>
                            <label>{t('marketplace:profileEdit.eduSpecialty')}<input value={eduForm.specialty} onChange={(e) => setEduForm({ ...eduForm, specialty: e.target.value })} /></label>
                        </div>
                        <button type="submit" className="sp-btn sp-btn--primary" style={{ marginTop: 12 }}>{t('common:add')}</button>
                    </form>
                </div>
            )}
        </OwnerPageLayout>
    );
}
